import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/auth/authService';
import tokenService from '../services/auth/tokenService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        console.log('🔧 [AuthContext] Initializing authentication...');
        
        // Check if user is authenticated and token is valid
        const isAuth = authService.isAuthenticated();
        console.log('🔧 [AuthContext] isAuthenticated:', isAuth);
        
        if (isAuth) {
          const storedUser = authService.getCurrentUserFromStorage();
          console.log('🔧 [AuthContext] storedUser:', storedUser ? 'Found' : 'Not found');
          console.log('🔧 [AuthContext] storedUser data:', storedUser);
          
          if (storedUser) {
            // Check if stored user has the required permissions structure
            const hasCompletePermissions = storedUser.permissions && 
                                         typeof storedUser.permissions === 'object' &&
                                         Array.isArray(storedUser.permissions.disciplines);
            
            if (hasCompletePermissions) {
              // IMMEDIATELY set the stored user to prevent UI flash (only if complete)
              console.log('🔧 [AuthContext] Setting stored user immediately (complete permissions)');
              setUser(storedUser);
              setIsAuthenticated(true);
            } else {
              console.log('🔧 [AuthContext] Stored user has incomplete permissions, waiting for server...');
            }
            
            // Always verify user data with server to get latest permissions
            try {
              console.log('🔧 [AuthContext] Verifying user with server...');
              const currentUser = await authService.getCurrentUser();
              console.log('🔧 [AuthContext] Server verification successful');
              // Update with fresh data from server
              setUser(currentUser);
              setIsAuthenticated(true);
            } catch (error) {
              console.error('❌ [AuthContext] Failed to verify user session:', error);
              
              // Only clear auth if it's a 401/403 (unauthorized) or 400 (bad token)
              // For network errors, keep the stored user data
              if (error.status === 401 || error.status === 403 || error.status === 400) {
                console.log('❌ [AuthContext] Token invalid, clearing auth data');
                await handleLogout();
              } else {
                console.log('⚠️ [AuthContext] Network error, keeping stored user data');
                // Keep the stored user data for network errors, but only if it has complete permissions
                if (hasCompletePermissions) {
                  setUser(storedUser);
                  setIsAuthenticated(true);
                } else {
                  console.log('⚠️ [AuthContext] Stored user has incomplete permissions, cannot fallback');
                  await handleLogout();
                }
              }
            }
          } else {
            // If no stored user but token exists, try to get current user
            try {
              console.log('🔧 [AuthContext] No stored user, fetching from server...');
              const currentUser = await authService.getCurrentUser();
              console.log('🔧 [AuthContext] Successfully fetched user from server');
              setUser(currentUser);
              setIsAuthenticated(true);
            } catch (error) {
              console.error('❌ [AuthContext] Failed to get current user:', error);
              // If getting current user fails, clear auth
              await handleLogout();
            }
          }
        } else {
          // Not authenticated, ensure clean state
          console.log('🔧 [AuthContext] User not authenticated, setting clean state');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ [AuthContext] Auth initialization error:', error);
        // For initialization errors, try to preserve stored user if available
        const storedUser = authService.getCurrentUserFromStorage();
        if (storedUser && authService.isAuthenticated()) {
          console.log('⚠️ [AuthContext] Initialization error but token exists, using stored user');
          setUser(storedUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } finally {
        setIsLoading(false);
        console.log('✅ [AuthContext] Authentication initialization complete');
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      // Deliberately does NOT touch isLoading. That flag means "still working
      // out who the user is on boot", and AppLayout swaps the whole route tree
      // for a spinner while it is true - which unmounted LoginPage mid-submit
      // and threw away the error it was about to display. LoginPage drives its
      // own local spinner for the button.
      const result = await authService.login(email, password);
      
      if (result.success) {
        const { user: userData } = result;
        // userData already contains permissions from authService.getCurrentUser()
        console.log('🔐 [AuthContext] Login successful, setting user data:', {
          username: userData.username,
          role: userData.role,
          hasDisciplines: userData.permissions?.disciplines?.length || 0
        });
        
        tokenService.setUser(userData);
        setUser(userData);
        setIsAuthenticated(true);
        
        // Add a small delay to ensure state is fully updated before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        return { success: true, user: userData };
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  // Handle logout (internal function)
  const handleLogout = useCallback(async () => {
    tokenService.clearAuth();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      setIsLoading(true);
      const result = await authService.register(userData);
      return result;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        // getCurrentUser() returns the user object with `permissions` already
        // merged in, and persists it to storage itself.
        const refreshedUser = await authService.getCurrentUser();
        setUser(refreshedUser);
        setIsAuthenticated(true);
        return refreshedUser;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      await handleLogout();
      throw error;
    }
  }, [handleLogout]);

  // Update user data locally (for optimistic updates)
  const updateUser = useCallback((updatedUserData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUserData
    }));
    // Also update in storage
    tokenService.setUser({
      ...user,
      ...updatedUserData
    });
  }, [user]);

  // Permission checking functions
  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  const hasAnyRole = useCallback((roles) => {
    return roles.includes(user?.role);
  }, [user]);

  const hasPermissionScope = useCallback((scope) => {
    return user?.permissionScope === scope;
  }, [user]);

  const canAccess = useCallback((requiredRoles = [], requiredPermissionScope = null) => {
    if (!isAuthenticated || !user) return false;
    
    // Check role requirements
    if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
      return false;
    }
    
    // Check permission scope requirements
    if (requiredPermissionScope && !hasPermissionScope(requiredPermissionScope)) {
      return false;
    }
    
    return true;
  }, [isAuthenticated, user, hasAnyRole, hasPermissionScope]);

  // Check if user can manage other users
  const canManageUsers = useCallback(() => {
    return hasAnyRole(['admin', 'manager']);
  }, [hasAnyRole]);

  // Check if user can create/edit data
  const canEdit = useCallback(() => {
    return hasAnyRole(['admin', 'manager', 'inspector']);
  }, [hasAnyRole]);

  // Check if user is organization-level or network/section-level
  const isOrganizationLevel = useCallback(() => {
    return user?.permissionScope === 'organization';
  }, [user]);

  const isNetworkSectionLevel = useCallback(() => {
    return user?.permissionScope === 'network_section';
  }, [user]);

  // First-time SSO user setup status
  const needsSetup = user?.setupStatus === 'pending_setup';
  const isPendingApproval = user?.setupStatus === 'pending_approval';

  // Get user's organization info
  const getUserOrganization = useCallback(() => {
    return {
      id: user?.organizationId,
      name: user?.organizationName
    };
  }, [user]);

  // Get user permissions summary
  const getUserPermissions = useCallback(() => {
    if (!user) return null;
    
    return {
      role: user.role,
      permissionScope: user.permissionScope,
      organizationId: user.organizationId,
      organizationName: user.organizationName,
      permissions: user.permissions || [],
      networks: user.networks || [],
      sections: user.sections || []
    };
  }, [user]);

  const hasDiscipline = useCallback((disciplineName) => {
    if (!user || !user.permissions?.disciplines) return false;
    // System admins have access to all disciplines
    if (user.role === 'admin' && user.organizationId === null) return true;
    return user.permissions.disciplines.some(d => d.name === disciplineName);
  }, [user]);

  const hasSubDiscipline = useCallback((subDisciplineName) => {
    if (!user || !user.permissions?.sub_disciplines) return false;
    // System admins have access to all sub-disciplines
    if (user.role === 'admin' && user.organizationId === null) return true;
    return user.permissions.sub_disciplines.includes(subDisciplineName);
  }, [user]);

  const value = {
    // State
    user,
    isLoading,
    isAuthenticated,
    
    // Auth functions
    login,
    logout,
    register,
    refreshUser,
    updateUser,
    
    // Permission functions
    hasRole,
    hasAnyRole,
    hasPermissionScope,
    canAccess,
    canManageUsers,
    canEdit,
    isOrganizationLevel,
    isNetworkSectionLevel,
    getUserOrganization,
    getUserPermissions,
    hasDiscipline,
    hasSubDiscipline,

    // Setup status (first-time SSO users)
    needsSetup,
    isPendingApproval,

    // Helper functions
    authService // Expose authService for direct access if needed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 